import React, { useState, useEffect } from 'react';
import './Dashboard.css'; // Import the CSS file for styles
import '@fortawesome/fontawesome-free/css/all.min.css'


interface DailyMetric {
  id: number;
  campaign_id: number;
  date: string;
  visitors: number;
  bid_requests: number;
  cost: string;
  cpc: string; // Already calculated CPC from the API
}


// Interfaces for Campaign, Bidding, and Creative
interface Campaign {
  id: number;
  campaign_name: string;
  ad_format: string;
  geo: string;
  traffic_source_type: string;
  bid: string;
  daily_cap: string;
  cost: string;
  impressions: number;
  clicks: number;
  conversions: number;
  win_rate: string;
  video_impressions: number;
}

interface Bidding {
  campaign_id: number;
  bid: string;
  daily_cap: string;
  cost: string;
}

interface Creative {
  creative_id: number;
  campaign_id: number;
  ad_name: string;
  title: string;
  description_1: string;
  description_2: string;
  display_url: string;
  destination_url: string;
}

const AdminPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [bidding, setBidding] = useState<Bidding[]>([]);
  const [creatives, setCreatives] = useState<Creative[]>([]);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [noData, setNoData] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<string>('Today');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedFilter, setSelectedFilter] = useState<string>('Select a Filter');
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [currentEditData, setCurrentEditData] = useState<Campaign | null>(null);
  const [dailyMetrics, setDailyMetrics] = useState<DailyMetric[]>([]);


  const handleMouseOver = (id: number) => {
    document.getElementById(`cog1_${id}`)!.className =
      document.getElementById(`cog2_${id}`)!.className =
      document.getElementById(`cog3_${id}`)!.className =
      "fa fa-cog fa-spin";
  };

  const handleMouseOut = (id: number) => {
    document.getElementById(`cog1_${id}`)!.className =
      document.getElementById(`cog2_${id}`)!.className =
      document.getElementById(`cog3_${id}`)!.className =
      "fa fa-cog";
  };



  const filterOptions = [
    'Select a Filters',
    'Traffic',
    'Geos',
    'Bid Range',
    'Type',
    'Status',
    'Device',
    'Editorial',
    'Requests',
    'Visits',
    'CTR',
    'Cost',
    'Archived',
    'Total Conversions',
    'Conversion Rate',
  ];

  const options = [
    'Today',
    'Yesterday',
    'Last 7 days',
    'This Month',
    'Last Month',
    'Last 30 days',
    'Select Date Range',
  ];

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const handleFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedFilter(event.target.value);
  };

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedOption(event.target.value);
    if (event.target.value === 'Select Date Range') {
      alert('Please implement the custom date range picker logic here.');
    }
  };

  // Function to fetch data from the server
  const fetchData = async () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates.');
      return;
    }

    try {
      const baseUrl = window.location.hostname === 'localhost'
      ? `http://localhost:5000/api/alldata/allDataTime?startDate=${startDate}&endDate=${endDate}`
      : `https://bidvertiser-api.vercel.app/api/alldata/allDataTime?startDate=${startDate}&endDate=${endDate}`;

    // Construct the API URL with the date range query parameters
    const apiUrl = `${baseUrl}`;


      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('Fetched data:', data);

      if (data.campaigns.length === 0 && data.dailyMetrics.length === 0) {
        setNoData(true);
      } else {
        setNoData(false);
      }

      setCampaigns(data.campaigns);
      setBidding(data.bidding);
      setCreatives(data.creatives);
      setDailyMetrics(data.dailyMetrics);

    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setNoData(true);
    }
  };



// Aggregate metrics for each campaign (no need to recalculate CPC)
const aggregateMetrics = (campaign: Campaign) => {
  const metrics = dailyMetrics.filter((m) => m.campaign_id === campaign.id);

  // Aggregate values
  const totalVisitors = metrics.reduce((sum, metric) => sum + metric.visitors, 0);
  const totalBidRequests = metrics.reduce((sum, metric) => sum + metric.bid_requests, 0);
  const totalCost = metrics.reduce((sum, metric) => sum + parseFloat(metric.cost), 0);

  // If there are no daily metrics, fall back to campaign cost
  const finalCost = metrics.length > 0 ? totalCost : parseFloat(campaign.cost);

  // Use the CPC directly from the API data (average CPC across metrics)
  const averageCPC =
    metrics.length > 0
      ? (metrics.reduce((sum, metric) => sum + parseFloat(metric.cpc), 0) / metrics.length).toFixed(2)
      : '0.00';

  return {
    totalVisitors,
    totalBidRequests,
    totalCost: finalCost,
    averageCPC, // Averaged CPC from daily metrics
  };
};

  // Function to save data as CSV
  const saveDataAsCSV = () => {
    if (campaigns.length === 0) {
      alert('No data available to save.');
      return;
    }

    const csvRows = [];
    const headers = ['Actions', 'Status', 'Ad Format', 'ID', 'Campaign Name', 'Ad Name', 'Your Bid (US$)', 'GEO', 'Bid Requests', 'Video Imp.', 'Visits', 'Win Rate', 'Cost', 'Daily Cap'];
    csvRows.push(headers.join(','));

    campaigns.forEach((campaign) => {
      const adNames = creatives
        .filter((creative) => creative.campaign_id === campaign.id)
        .map((creative) => creative.ad_name)
        .join('; ');

      const row = [
        '⚙️',
        '', // Status
        campaign.ad_format,
        campaign.id,
        campaign.campaign_name,
        adNames, // Ad Names for the campaign
        campaign.bid,
        campaign.geo,
        campaign.impressions,
        campaign.video_impressions,
        campaign.clicks,
        campaign.win_rate,
        campaign.cost,
        campaign.daily_cap,
      ];
      csvRows.push(row.join(','));
    });

    const csvData = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const csvUrl = window.URL.createObjectURL(csvData);
    const link = document.createElement('a');
    link.setAttribute('href', csvUrl);
    link.setAttribute('download', 'campaigns.csv');
    link.click();
  };

  const clearData = () => {
    setCampaigns([]);
    setNoData(false);
  };


  const handleEdit = (campaign: Campaign) => {
    setCurrentEditData(campaign);
    setIsEditModalOpen(true);
  };

  const handleUpdate = async () => {
    if (currentEditData) {
      try {
        const response = await fetch(`https://bidvertiser-api.vercel.app/api/campaigns/updateCampaign`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(currentEditData),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        alert('Campaign updated successfully!');
        setIsEditModalOpen(false);
        fetchData();
      } catch (error) {
        console.error('Error updating campaign:', error);
        alert('Failed to update campaign.');
      }
    }
  };


  return (
    <>



      {/* --------------------------------------------------------------------------------------------------------------------------------------------------------------------- */}
      {/* TOp section filter */}
      {/* --------------------------------------------------------------------------------------------------------------------------------------------------------------------- */}

      <div className="dashboard-header">
        {/* Date Range */}
        <div className="date-range">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <select
              value={selectedOption}
              onChange={handleChange}
              className='date-picker'
              style={{
                padding: '6.5px',
                border: '1px solid #ccc',
                borderRight: '0px',
                marginRight: '-5px',
                borderRadius: '0px',
                cursor: 'pointer',
              }}
            >
              {options.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <input
            type="date"
            className="date-picker"
            style={{ marginRight: '-5px', borderRight: '0px' }}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)} // Update start date
          />
          <input
            type="date"
            className="date-picker"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)} // Update end date
          />
          <button className="btn btn-generate" onClick={fetchData}>
            Generate
          </button>
        </div>

        {/* Filters and Bulk Actions */}
        <div className="actions">

          <div style={{ position: 'relative' }}>
            {/* Filter Button */}
            <button
              onClick={toggleModal}
              style={{
                backgroundColor: '#1173B7',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}

            >
              <img style={{ width: '13px', marginRight: '4px', marginBottom: '-2px' }} src="https://my.bidvertiser.com/BidVertiser/Images/bdv_action_filter.png" alt="" />Filters
            </button>

            {/* Modal Background */}
            {isModalOpen && (
              <div
                style={{
                  position: 'fixed',
                  top: 60,
                  left: -400,
                  width: '100%',
                  height: '100%',
                  // backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  zIndex: 1000,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  paddingTop: '100px',
                }}
              >
                {/* Modal Content */}
                <div
                  style={{
                    backgroundColor: '#1B3A5E',
                    padding: '10px',
                    borderRadius: '8px',
                    width: '400px',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                    position: 'relative',
                    height: '50px'
                  }}
                >
                  {/* <h4 style={{ marginBottom: '10px', fontSize: '16px', color: '#333' }}>Select a Filter</h4> */}
                  <select
                    value={selectedFilter}
                    onChange={handleFilterChange}
                    style={{
                      width: '40%',
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      marginBottom: '10px',
                      fontSize: '14px',
                    }}
                  >
                    <option value="Select a Filter" disabled>

                    </option>
                    {filterOptions.map((option, index) => (
                      <option key={index} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>



                  {/* Close Button */}
                  <button
                    onClick={toggleModal}
                    style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      background: 'transparent',
                      border: 'none',
                      fontSize: '16px',
                      cursor: 'pointer',
                      color: '#999',
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>
            )}
          </div>
          <button className="btn btn-primary"> <i style={{ marginRight: '5px' }} className='fa fa-asterisk'></i>Bulk Actions</button>

        </div>

        {/* Create New Campaign */}
        <div className="create-campaign">
          <button className="btn btn-primary">Create new campaign</button>
          <a href="#" className="video-guide">
            <span>📹</span> Video Guide
          </a>
        </div>
      </div>



      {/* --------------------------------------------------------------------------------------------------------------------------------------------------------------------- */}
      {/* TOp section filter  THE END*/}
      {/* --------------------------------------------------------------------------------------------------------------------------------------------------------------------- */}


      {/* --------------------------------------------------------------------------------------------------------------------------------------------------------------------- */}
      {/* Number of data display filter  */}
      {/* --------------------------------------------------------------------------------------------------------------------------------------------------------------------- */}




      <div className="table-footer" style={{ fontSize: '14px' }}>
        <p style={{ fontSize: '12px' }}>Show</p>
        <select style={{ padding: '10px', borderRadius: '3px', borderColor: '#D3D3D3' }}>
          <option value="50">50</option>
          <option value="100">100</option>
        </select>
        Campaigns per page out of {campaigns.length}
      </div>




      {/* --------------------------------------------------------------------------------------------------------------------------------------------------------------------- */}
      {/* Number of data display filter  THE END*/}
      {/* --------------------------------------------------------------------------------------------------------------------------------------------------------------------- */}


      <div className="campaign-table-container">
        {/* Actions Header */}
        <div className="table-actions">
          <button className="btn-red" onClick={clearData}>
            Clear
          </button>
          <button className="btn-green" onClick={fetchData}>
            Refresh
          </button>
          <button className="btn-save" onClick={saveDataAsCSV}>
            Save
          </button>
        </div>

        {/* Check if no data and show a message */}
        {noData ? (
          <div className="no-data-message">No data available for the selected date range.</div>
        ) : (
          <table className="campaign-table">
            <thead>
              <tr>
                <th>Actions</th>
                <th>Status</th>
                <th>Ad Format</th>
                <th>ID</th>
                <th>Campaign Name</th>
                <th>Your Bid (US$)</th>
                <th>GEO</th>
                <th>Bid Requests</th>
                <th>Video Imp.</th>
                <th>Visits</th>
                <th>Win Rate</th>
                <th>Cost</th>
                <th>Daily Cap</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((campaign) => {
                const { totalVisitors, totalBidRequests, totalCost, averageCPC } = aggregateMetrics(campaign);
                return (
                  <tr key={campaign.id}>
                    <td style={{ width: '160px' }}>
                      <div className='ActiosIcon' style={{ display: "flex", alignContent: 'center', textAlign: 'center', gap: '2px' }}>
                        <i style={{ color: '#E4EEF9' }} className="fa fa-bars"></i>
                        <i style={{ color: '#FC7C7C' }} className='fa fa-step-forward'></i>
                        <i aria-hidden="true" className='fa fa-pencil fa-fw' ></i>
                        <i className='fa fa-copy fa-fw'></i>
                        <i className='fa fa-archive fa-fw'></i>
                        <button
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#155C93',
                            cursor: 'pointer',
                            marginTop: '-20px'
                          }}
                          onClick={() => handleEdit(campaign)}
                        >
                          <div
                            // name="More_button"
                            title="Advanced Settings"
                            id={`more_funcs_${campaign.id}`}
                            style={{ position: "relative", margin: "4px 0 0 2px" }}
                            onMouseOver={() => handleMouseOver(campaign.id)}
                            onMouseOut={() => handleMouseOut(campaign.id)}
                            onClick={() => handleEdit(campaign)}
                          >
                            <i
                              className="fa fa-cog"
                              id={`cog1_${campaign.id}`}
                              aria-hidden="true"
                              style={{
                                color: "var(--greyBlue)",
                                position: "absolute",
                                top: "0",
                                left: "14px",
                                fontSize: "7px",
                              }}
                            ></i>
                            <i
                              className="fa fa-cog"
                              id={`cog2_${campaign.id}`}
                              aria-hidden="true"
                              style={{
                                color: "var(--greyBlue)",
                                position: "absolute",
                                top: "9px",
                                left: "15px",
                                fontSize: "8px",
                              }}
                            ></i>
                            <i
                              className="fa fa-cog"
                              id={`cog3_${campaign.id}`}
                              aria-hidden="true"
                              style={{
                                color: "var(--greyBlue)",
                                position: "absolute",
                                top: "2px",
                                left: "3px",
                                fontSize: "14px",
                              }}
                            ></i>
                          </div>
                        </button>
                      </div>

                    </td>
                    <td className='ActiosIcon' >
                      <i style={{ marginRight: '5px' }} className='fa fa-minus-circle'></i>
                      {/* <i className='fa fa-circle-thin '></i> */}
                      <i style={{ marginRight: '5px' }} className='fa fa-stethoscope fa-fw'></i>
                      <i style={{ color: '#FEAA15' }} className='fa fa-exclamation-triangle'></i>
                    </td>

                    <td>
                      <div style={{ display: 'flex', gap: '10px', textTransform: 'capitalize' }} className='ActiosIcon'>
                        <div style={{ border: 'solid', width: '20px', borderWidth: '0.2px', borderRadius: '50px,', height: '20px', alignContent: 'center', textAlign: 'center' }}>
                          M
                        </div>
                        <i className='fa fa-desktop'></i>
                        {campaign.ad_format}

                      </div>

                    </td>

                    <td>{campaign.id}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '5px' }} className='ActiosIcon'>
                        <i style={{ color: '#62A435' }} className='fa fa-bar-chart'></i>
                        {/* {creatives
                          .filter((creative) => creative.campaign_id === campaign.id)
                          .map((creative) => (
                            <div key={creative.campaign_id}>{creative.ad_name}</div>
                          ))} */}
                          {campaign.campaign_name}
                      </div>

                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                        <i style={{ color: '#FEAA15' }} className='fa fa-exclamation-triangle'></i>
                        <div style={{ color: '#81D34A', fontWeight: '500' }}>
                          <p>$</p>
                        </div>
                        <div className='BidBorder' style={{ borderColor: '#E4EEF9' }}>
                          {Number(campaign.bid).toFixed(2)}
                        </div>

                      </div>

                    </td>

                    <td>{campaign.geo}</td>
                    <td>{campaign.impressions}</td>
                    <td>{totalBidRequests}</td>
                    <td>{totalVisitors}</td>
                    <td>{Number(campaign.win_rate).toFixed(2)}%</td>
                    <td>${totalCost.toFixed(2)}</td>
                    <td>
                      <div style={{ display: 'flex' }}>
                        <div style={{ color: '#81D34A', fontWeight: '500', marginRight: '5px' }}>
                          <p>$</p>
                        </div>
                        <div className='BidBorder' style={{ borderColor: '#E4EEF9' }}>
                        {averageCPC}
                        </div>

                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

        )}

        {/* Pagination */}

      </div>
      {isEditModalOpen && currentEditData && (
        <div className="modal" style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          zIndex: 1000,
        }}>
          <h2>Edit Campaign</h2>
          <form>
            <label>GEO:</label>
            <input
              type="text"
              value={currentEditData.geo}
              onChange={(e) => setCurrentEditData({ ...currentEditData, geo: e.target.value })}
            />
            <label>Bid Requests:</label>
            <input
              type="number"
              value={currentEditData.impressions}
              onChange={(e) => setCurrentEditData({ ...currentEditData, impressions: parseInt(e.target.value) })}
            />
            <label>Video Imp.:</label>
            <input
              type="number"
              value={currentEditData.video_impressions}
              onChange={(e) => setCurrentEditData({ ...currentEditData, video_impressions: parseInt(e.target.value) })}
            />
            <label>Visits:</label>
            <input
              type="number"
              value={currentEditData.clicks}
              onChange={(e) => setCurrentEditData({ ...currentEditData, clicks: parseInt(e.target.value) })}
            />
            <label>Win Rate:</label>
            <input
              type="text"
              value={currentEditData.win_rate}
              onChange={(e) => setCurrentEditData({ ...currentEditData, win_rate: e.target.value })}
            />
          </form>
          <button onClick={handleUpdate}>Save</button>
          <button onClick={() => setIsEditModalOpen(false)}>Cancel</button>
        </div>
      )}
    </>
  );
};

export default AdminPage;
